if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
      this.products = new Map(this.data.products.map((product) => [String(product.productId), product]));
      this.currentProduct = this.products.get(String(this.data.currentProductId));
      if (!this.currentProduct) return;
      this.bundleMode = this.dataset.bundleMode || 'standard';
      this.strings = JSON.parse(this.dataset.bundleStrings || '{}');

      this.packCount = 1;
      this.selections = [this.currentProduct];
      this.activeSlot = null;
      this.modal = this.querySelector('[data-bundle-modal]');
      this.dialog = this.modal.querySelector('[role="dialog"]');
      this.builder = this.querySelector('.bundle-builder__builder');
      this.form = this.closest('[id^="ProductInfo-"]')?.querySelector('product-form form');
      this.productForm = this.form?.closest('product-form');
      this.submitButton = this.form?.querySelector('[type="submit"][name="add"]');
      this.submitLabel = this.submitButton?.querySelector('span');
      this.originalSubmitLabel = this.submitLabel?.textContent.trim();
      this.originalSubmitDisabled = Boolean(this.submitButton?.disabled);
      this.stickyButton = this.closest('.section')?.querySelector('.sticky-cta .button');
      this.stickyLabel = this.stickyButton?.querySelector('span');
      this.originalStickyLabel = this.stickyLabel?.textContent.trim();
      this.originalStickyDisabled = Boolean(this.stickyButton?.disabled);
      this.stickyPrice = this.closest('.section')?.querySelector('.sticky-cta__price');
      this.originalStickyPrice = this.stickyPrice?.innerHTML;
      this.stickyTitles = [...(this.closest('.section')?.querySelectorAll('.sticky-cta__title') || [])];
      this.originalStickyTitles = this.stickyTitles.map((title) => title.textContent);
      this.summaryTitle = this.querySelector('[data-bundle-summary-title]');
      this.quantityControls = [
        this.closest('[id^="ProductInfo-"]')?.querySelector(`#Quantity-Form-${this.dataset.sectionId}`),
        this.form?.querySelector('.product-form__buttons .quantity')?.parentElement,
      ].filter(Boolean);

      this.querySelectorAll('[data-pack-count]').forEach((button) => {
        button.addEventListener('click', () => this.selectPack(Number(button.dataset.packCount)));
      });
      this.querySelector('[data-bundle-products]').addEventListener('click', (event) => {
        const button = event.target.closest('[data-bundle-product-id]');
        if (!button || button.disabled) return;
        this.chooseProduct(button.dataset.bundleProductId);
      });
      this.querySelectorAll('[data-bundle-close]').forEach((button) => button.addEventListener('click', () => this.closeModal()));
      this.modal.addEventListener('keydown', (event) => this.onModalKeydown(event));
      this.form?.addEventListener('submit', (event) => this.onSubmit(event), true);
      this.render();
    }

    selectPack(packCount) {
      this.packCount = packCount;
      this.selections = this.selections.slice(0, packCount);
      while (this.selections.length < packCount) this.selections.push(null);
      this.render();
    }

    chooseProduct(productId) {
      const product = this.products.get(String(productId));
      if (!product?.available || this.activeSlot === null) return;
      this.selections[this.activeSlot] = product;
      this.closeModal();
      this.render();
    }

    openModal(slotIndex, trigger) {
      this.activeSlot = slotIndex;
      this.modalTrigger = trigger;
      this.modal.hidden = false;
      document.body.classList.add('bundle-modal-open');
      this.dialog.focus();
    }

    closeModal() {
      if (this.modal.hidden) return;
      this.modal.hidden = true;
      document.body.classList.remove('bundle-modal-open');
      this.modalTrigger?.focus();
      this.activeSlot = null;
    }

    onModalKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.modal.querySelectorAll('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')]
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    render() {
      this.querySelectorAll('[data-pack-count]').forEach((button) => {
        const selected = Number(button.dataset.packCount) === this.packCount;
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
      });
      this.querySelectorAll('[data-bundle-price-for]').forEach((price) => {
        price.hidden = Number(price.dataset.bundlePriceFor) !== this.packCount;
      });
      this.updateBundleTitles();
      this.updateStickyPrice();
      this.builder.hidden = this.packCount === 1;
      this.toggleDynamicCheckout(this.packCount > 1);
      this.quantityControls.forEach((control) => { control.hidden = this.packCount > 1; });
      if (this.packCount === 1) {
        const label = this.currentProduct.available ? this.strings.addToCart : this.originalSubmitLabel;
        const stickyLabel = this.currentProduct.available ? label : this.originalStickyLabel;
        this.setButtonState(this.originalSubmitDisabled, label, stickyLabel, this.originalStickyDisabled);
        return;
      }

      const selectedCount = this.selections.filter(Boolean).length;
      this.querySelector('[data-bundle-heading]').textContent = this.format(this.strings.heading, { count: this.packCount });
      this.querySelector('[data-bundle-progress]').textContent = this.format(this.strings.progress, { selected: selectedCount, count: this.packCount });
      const slots = this.querySelector('[data-bundle-slots]');
      slots.dataset.count = this.packCount;
      slots.replaceChildren(...this.selections.map((product, index) => this.createSlot(product, index)));

      const missing = this.packCount - selectedCount;
      const label = missing
        ? this.format(this.strings.choosePatterns, { count: missing })
        : this.strings.addToCart;
      this.setButtonState(missing > 0 || !this.currentProduct.available, label, label);
    }

    updateBundleTitles() {
      const bundleTitle = this.bundleMode === 'three_for_two' && this.packCount === 3
        ? this.strings.summaryThreeForTwo
        : this.format(this.strings.summaryStandard, { count: this.packCount * 3 });
      if (this.summaryTitle) this.summaryTitle.textContent = bundleTitle;
      this.stickyTitles.forEach((title, index) => {
        title.textContent = this.packCount === 1
          ? this.originalStickyTitles[index]
          : bundleTitle;
      });
    }

    updateStickyPrice() {
      if (!this.stickyPrice) return;
      if (this.packCount === 1) {
        this.stickyPrice.innerHTML = this.originalStickyPrice || '';
        return;
      }
      const selectedPrice = this.querySelector(`[data-bundle-price-for="${this.packCount}"]`);
      if (selectedPrice) this.stickyPrice.innerHTML = selectedPrice.innerHTML;
    }

    createSlot(product, index) {
      const slot = document.createElement(product ? 'div' : 'button');
      slot.className = `bundle-builder__slot${product ? '' : ' bundle-builder__slot--empty'}`;
      if (!product) {
        slot.type = 'button';
        slot.textContent = this.strings.choosePattern;
        slot.addEventListener('click', () => this.openModal(index, slot));
        return slot;
      }

      if (product.image) {
        const imageWrap = document.createElement('span');
        imageWrap.className = 'bundle-builder__slot-image media media--square';
        const image = document.createElement('img');
        image.src = product.image;
        image.alt = '';
        image.loading = 'lazy';
        imageWrap.append(image);
        slot.append(imageWrap);
      }
      const title = document.createElement('strong');
      title.className = 'bundle-builder__slot-title';
      title.textContent = product.title;
      slot.append(title);
      if (product.variantTitle) {
        const variant = document.createElement('span');
        variant.className = 't7 block';
        variant.textContent = product.variantTitle;
        slot.append(variant);
      }
      if (index === 0) {
        const first = document.createElement('span');
        first.className = 't7 block mt-025';
        first.textContent = this.strings.firstChoice;
        slot.append(first);
      } else {
        const change = document.createElement('button');
        change.type = 'button';
        change.className = 'bundle-builder__change link underlined-link';
        change.textContent = this.strings.change;
        change.addEventListener('click', () => this.openModal(index, change));
        slot.append(change);
      }
      return slot;
    }

    format(template, values) {
      return Object.entries(values).reduce(
        (text, [key, value]) => text.replaceAll(`[${key}]`, value),
        template,
      );
    }

    toggleDynamicCheckout(hidden) {
      const accelerated = this.form?.querySelector('.shopify-payment-button, shopify-accelerated-checkout');
      if (accelerated) accelerated.hidden = hidden;
    }

    setButtonState(disabled, mainLabel, stickyLabel, stickyDisabled = disabled) {
      if (this.submitButton) {
        this.submitButton.disabled = disabled;
        this.submitButton.setAttribute('aria-disabled', String(disabled));
      }
      if (this.submitLabel && mainLabel) this.submitLabel.textContent = mainLabel;
      if (this.stickyButton) {
        this.stickyButton.disabled = stickyDisabled;
        this.stickyButton.setAttribute('aria-disabled', String(stickyDisabled));
      }
      if (this.stickyLabel && stickyLabel) this.stickyLabel.textContent = stickyLabel;
    }

    async onSubmit(event) {
      if (this.packCount === 1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.adding || this.selections.some((product) => !product) || this.submitButton?.disabled) return;
      await this.addBundle();
    }

    async addBundle() {
      const bundleId = `${Date.now().toString(36)}-${window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)}`;
      const sections = this.sectionsToRender();
      const items = this.selections.map((product, index) => {
        const item = { id: product.variantId, quantity: 1 };
        if (this.bundleMode !== 'three_for_two') {
          item.properties = {
            _bundle_id: bundleId,
            _bundle_type: String(this.packCount * 3),
            _bundle_source: 'custom_bundle',
            _bundle_discount_key: `custom_bundle_${this.packCount * 3}`,
            _bundle_position: String(index + 1),
          };
        }
        return item;
      });
      this.setLoading(true);
      this.showError();
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      if (theme.settings.cartDrawer.atcAction === 'drawer') cartDrawer?.setActiveElement(document.activeElement);
      if (theme.settings.cartDrawer.atcAction === 'notification') cartNotification?.setActiveElement(document.activeElement);
      try {
        const response = await fetch(routes.cart_add_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            items,
            sections: [...new Set(sections.map((section) => section.id))],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || window.cartStrings.error);
        this.renderCart(data);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: data });
        this.submitButton?.classList.add('added-to-cart');
        this.stickyButton?.classList.add('added-to-cart');
        setTimeout(() => {
          this.submitButton?.classList.remove('added-to-cart');
          this.stickyButton?.classList.remove('added-to-cart');
        }, 1000);
      } catch (error) {
        this.showError(error.message);
      } finally {
        this.setLoading(false);
        publish('quick-buy-action-complete');
      }
    }

    sectionsToRender() {
      const sections = [];
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartItems = document.querySelector('cart-items');
      if (cartDrawer) sections.push(...cartDrawer.getSectionsToRender());
      if (cartNotification) sections.push(...cartNotification.getSectionsToRender());
      if (cartItems) sections.push(...cartItems.getSectionsToRender().map((section) => ({ id: section.section })));
      return sections;
    }

    renderCart(data) {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');
      const cartItems = document.querySelector('cart-items');
      if (theme.settings.cartDrawer.atcAction === 'page') {
        window.location = routes.cart_url;
        return;
      }
      cartDrawer?.classList.remove('is-empty');
      cartNotification?.classList.remove('is-empty');
      cartDrawer?.renderContents(data);
      cartItems?.renderContents(data);
      if (cartNotification) {
        const notificationData = { ...data, key: data.key || data.items?.[0]?.key };
        cartNotification.renderContents(notificationData);
      }
      if (!cartDrawer && !cartItems) window.syncFreeShippingBars?.(data.sections);
    }

    setLoading(loading) {
      this.adding = loading;
      [this.submitButton, this.stickyButton].filter(Boolean).forEach((button) => {
        button.classList.toggle('loading', loading);
        button.setAttribute('aria-disabled', String(loading));
        button.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      });
    }

    showError(message = '') {
      const wrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) return;
      wrapper.hidden = !message;
      const output = wrapper.querySelector('.product-form__error-message');
      if (output) output.textContent = message;
    }

  });
}
